import { keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";
import type { PageCtaPageKey, PageCtaPublicItem } from "@shared/pageCtaSlots";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { usePortalQuery } from "@/state/hooks/usePortalQuery";
import { queryKeys } from "@/state/queryKeys";

/**
 * One `pageCta.listForPage` per pageKey (shared across TOP_BANNER / AFTER_FIRST_ROW slots).
 */
export function usePageCtas(pageKey: PageCtaPageKey, enabled = true) {
  const query = usePortalQuery({
    queryKey: queryKeys.portal.list("pageCta", { pageKey }),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PageCtaPublicItem[]> => {
      try {
        const res = await trpc.pageCta.listForPage.query({ pageKey });
        return res.items ?? [];
      } catch (err) {
        handleTrpcError(err);
        return [];
      }
    },
  });

  return {
    items: query.data ?? [],
    loading: query.isInitialLoading,
    reload: () => void query.refetch(),
  };
}

export function usePageCtasForSlot(pageKey: PageCtaPageKey, slotKey: string, enabled = true) {
  const { items, loading, reload } = usePageCtas(pageKey, enabled);
  const slotItems = useMemo(
    () => items.filter((item) => item.slotKey === slotKey),
    [items, slotKey],
  );
  return { items: slotItems, loading, reload };
}
